import { useState } from "react";
import { ReactComponent as CollapseUp } from "../../../../assets/svgs/collapseup.svg";
import { ReactComponent as CollapseDown } from "../../../../assets/svgs/collapsedown.svg";

export const QuestionForm = ({ questions }) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const toggleGroup = (id) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const labelStyles = {
    color: "rgba(0, 0, 0, 0.88)",
    fontWeight: "400",
    marginBottom: "5px",
    fontSize: "13px",
    display: "block"
  };

  const commonStyles = {
    width: "100%",
    padding: "10px",
    marginTop: "8px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "300",
    backgroundColor: "#06513D1A",
    boxSizing: "border-box",
    outline: "none"
  };

  const renderFieldByType = (question) => {
    const id = question._id || question.label || Math.random();

    switch (question.type) {
      case "text":
      case "percentage":
      case "number":
      case "password":
      case "email":
      case "tel":
      case "url":
      case "search":
      case "date":
      case "time":
      case "week":
      case "month":
      case "datetime-local":
      case "color":
      case "range":
        return (
          <input
            id={id}
            type={question.type}
            placeholder="Please Enter"
            style={commonStyles}
          />
        );

      case "checkbox":
      case "radio":
        return (
          <label>
            <input type={question.type} />
            <span className="ml-2">{question.label || question.question}</span>
          </label>
        );

      case "multi-select":
        return (
          <>
            {question.options?.map((option) => (
              <label
                key={option._id}
                style={{ display: "block", marginBottom: "5px" }}
              >
                <input type="checkbox" value={option.value} />
                <span className="ml-2">{option.key}</span>
              </label>
            ))}
          </>
        );

      case "select":
        return (
          <select style={commonStyles}>
            <option value="">-- Select an option --</option>
            {question.options?.map((option) => (
              <option key={option._id} value={option.value}>
                {option.key}
              </option>
            ))}
          </select>
        );

      case "file":
        return <input type="file" style={{ marginTop: "8px" }} />;
      case "title":
        return <label>{question.label}</label>;

      default:
        return (
          <p style={{ color: "red" }}>
            Unsupported field type: <strong>{question.type}</strong>
          </p>
        );
    }
  };

  return (
    <div className="">
      <form>
        {questions.map((group) => {
          const groupId = group.id;
          const groupQuestions = group.questions || [];
          const isParentGroup = group.title !== null;
          const isExpanded = expandedGroups[groupId];

          if (!groupQuestions || groupQuestions.length === 0) return null;

          // When group has a title (true parent group)
          if (isParentGroup) {
            const parentCheckboxId = group._id || group.id || group.title;

            return (
              <div
                key={groupId}
                className="px-10 border border-[#3BA2B959] rounded-none"
                style={{ marginBottom: "25px", padding: "16px" }}
              >
                {/* Parent Title */}
                <div className="flex justify-between items-start gap-4">
                  <div className="flex gap-2 items-start w-full">
                    <label
                      style={{
                        ...labelStyles,
                        fontWeight: "bold",
                        fontSize: "15px"
                      }}
                    >
                      {group.id}. {group.title}
                    </label>
                  </div>

                  {groupQuestions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(groupId)}
                      className="mt-1 px-3 py-1 text-sm"
                    >
                      {isExpanded ? (
                        <CollapseDown className="w-6 h-6" />
                      ) : (
                        <CollapseUp className="w-6 h-6" />
                      )}
                    </button>
                  )}
                </div>

                {isExpanded && (
                  <div
                    className="mt-5 p-4 ml-6 rounded-xl"
                    style={{
                      backgroundColor: "#06513D1A",
                      border: "1px solid #06513D59"
                    }}
                  >
                    {groupQuestions.map((child, i) => (
                      <div key={i} className="mb-4">
                        {child.label && (
                          <label style={labelStyles}>
                            <b>{child.questionId}.</b> {child.label}
                          </label>
                        )}
                        {renderFieldByType(child)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          //  Flat custom question (1 question only, no title)
          if (groupQuestions.length === 1) {
            const question = groupQuestions[0];
            return (
              <div
                key={groupId}
                className="px-10 border border-[#3BA2B959] rounded-none"
                style={{ marginBottom: "25px", padding: "16px" }}
              >
                <div className="mb-4">
                  {question.label && (
                    <label style={labelStyles}>
                      <b>{question.questionId}</b> {question.label}
                    </label>
                  )}
                  {renderFieldByType(question)}
                </div>
              </div>
            );
          }

          // When group has no title (first question is the parent)
          const parentQuestion = groupQuestions[0];
          const parentId =
            parentQuestion?._id || parentQuestion?.label || groupId;
          const parentLabel = parentQuestion?.label;
          const childQuestions = groupQuestions.slice(1);

          return (
            <div
              key={groupId}
              className="px-10 border border-[#3BA2B959] rounded-none"
              style={{ marginBottom: "25px", padding: "16px" }}
            >
              {/* First question as parent */}
              <div className="flex justify-between items-start gap-4">
                <div className="w-full">
                  <div className="flex items-start gap-2 pb-5">
                    <label style={labelStyles}>
                      <b>{parentQuestion?.questionId || ""}</b> {parentLabel}
                    </label>
                  </div>
                </div>

                {childQuestions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupId)}
                    className="mt-7 px-3 py-1 text-sm"
                  >
                    {isExpanded ? (
                      <CollapseDown className="w-6 h-6" />
                    ) : (
                      <CollapseUp className="w-6 h-6" />
                    )}
                  </button>
                )}
              </div>

              {isExpanded && childQuestions.length > 0 && (
                <div
                  className="mt-5 p-4 ml-20 rounded-xl"
                  style={{
                    backgroundColor: "#06513D1A",
                    border: "1px solid #06513D59"
                  }}
                >
                  {childQuestions.map((child, i) => (
                    <div key={i} className="mb-4">
                      {child.label && (
                        <label style={labelStyles}>
                          <b>{child.questionId}.</b> {child.label}
                        </label>
                      )}
                      {renderFieldByType(child)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </form>
    </div>
  );
};
